import { accountVariables } from "@store/fast-versions/data/account-variables";
import type { PlanTier, VariableValueType } from "../variable-picker/types";

/** Where a default variable's value comes from at render (resolution wiring is
 * a separate, later task — this tag is forward-looking). */
export type StorydocVariableSource = "account" | "system" | "input";

export interface StorydocVariable {
  /** Token key — inserted as `{{key}}`. */
  key: string;
  label: string;
  /** Category id — must match a STORYDOC_CATEGORIES entry. */
  category: string;
  plan: PlanTier;
  type: VariableValueType;
  source: StorydocVariableSource;
}

export interface StorydocCategoryMeta {
  id: string;
  label: string;
}

/** Category order drives the picker's grouping order. */
export const STORYDOC_CATEGORIES: StorydocCategoryMeta[] = [
  { id: "prospect", label: "Prospect" },
  { id: "company", label: "Company" },
  { id: "sender", label: "Sender" },
  { id: "organization", label: "Organization" },
  { id: "proposal-deal", label: "Proposal / Deal" },
  { id: "product", label: "Product" },
  { id: "ai-content", label: "AI Content" },
  { id: "media", label: "Media" },
  { id: "social-proof", label: "Social Proof" },
  { id: "content", label: "Content" },
  { id: "time-date", label: "Time / Date" },
  { id: "date-operations", label: "Date Operations" },
  { id: "design", label: "Design" },
];

const v = (
  key: string,
  label: string,
  category: string,
  plan: PlanTier,
  source: StorydocVariableSource,
  type: VariableValueType = "input",
): StorydocVariable => ({ key, label, category, plan, type, source });

// Profile variables are free on every plan (plan-rules: account variables
// come first), so the catalogue says so too.
const SENDER_VARIABLES: StorydocVariable[] = accountVariables
  .filter((entry) => entry.variable.name.startsWith("sender_"))
  .map(({ variable }) =>
    v(
      variable.name,
      variable.title,
      "sender",
      "starter",
      "account",
      variable.type.includes("image") ? "image" : "input",
    ),
  );

export const STORYDOC_VARIABLES: StorydocVariable[] = [
  // Prospect
  v("prospect_first_name", "First name", "prospect", "starter", "input"),
  v("prospect_last_name", "Last name", "prospect", "starter", "input"),
  v("prospect_full_name", "Full name", "prospect", "pro", "input", "computed"),
  v("prospect_email", "Email", "prospect", "starter", "input"),
  v("prospect_photo", "Photo", "prospect", "pro", "input", "image"),
  v("prospect_job_title", "Job title", "prospect", "starter", "input"),
  v("prospect_ai_summary", "AI summary", "prospect", "pro", "input", "ai"),
  // Company
  v("company", "Company", "company", "starter", "input"),
  v("website", "Website", "company", "pro", "input"),
  v("location", "Location", "company", "pro", "input"),
  v(
    "ai_company_description",
    "Company description",
    "company",
    "pro",
    "input",
    "ai",
  ),
  // Sender: the profile variables themselves (account-variables.ts), so
  // Storydoc → Sender and Variables → Profile variables are one list. The
  // org's profile custom fields join this category at runtime
  // (withProfileFields).
  ...SENDER_VARIABLES,
  // Organization (account-sourced)
  v("organization_name", "Org name", "organization", "starter", "account"),
  v("organization_logo", "Org logo", "organization", "pro", "account", "image"),
  v("organization_website", "Org website", "organization", "pro", "account"),
  // Proposal / Deal
  v("deal_value", "Amount", "proposal-deal", "pro", "input"),
  v("discount", "Discount", "proposal-deal", "pro", "input"),
  v(
    "offer_expiry_date",
    "Expiry date",
    "proposal-deal",
    "pro",
    "input",
    "date",
  ),
  v("contract_length", "Contract length", "proposal-deal", "pro", "input"),
  v("kickoff_date", "Kick-off date", "proposal-deal", "pro", "input", "date"),
  v("payment_link", "Payment link", "proposal-deal", "pro", "input"),
  // Product
  v("product_name", "Product name", "product", "pro", "input"),
  // AI Content
  v(
    "ai_personalized_intro",
    "Personalized intro",
    "ai-content",
    "pro",
    "input",
    "ai",
  ),
  v(
    "ai_value_proposition",
    "Value proposition",
    "ai-content",
    "pro",
    "input",
    "ai",
  ),
  // Media
  v("company_logo", "Company logo", "media", "pro", "input", "image"),
  v("hero_image", "Hero image", "media", "pro", "input", "image"),
  v("cover_image", "Cover image", "media", "pro", "input", "image"),
  v("case_study_image", "Case study image", "media", "pro", "input", "image"),
  v("testimonial_photo", "Testimonial photo", "media", "pro", "input", "image"),
  v(
    "product_screenshot",
    "Product screenshot",
    "media",
    "pro",
    "input",
    "image",
  ),
  v("team_photo", "Team photo", "media", "pro", "input", "image"),
  v(
    "local_office_photo",
    "Local office photo",
    "media",
    "pro",
    "input",
    "image",
  ),
  // Social Proof
  v("testimonial_quote", "Quote", "social-proof", "pro", "input"),
  v("testimonial_author", "Author", "social-proof", "pro", "input"),
  v("testimonial_role", "Role", "social-proof", "pro", "input"),
  // Content
  v("welcome_message", "Welcome message", "content", "starter", "input"),
  v("next_steps", "Next steps", "content", "pro", "input"),
  v("custom_text", "Custom text", "content", "pro", "input"),
  // Time / Date
  v("current_date", "Current date", "time-date", "starter", "system", "system"),
  v("current_month", "Current month", "time-date", "pro", "system", "system"),
  v("current_year", "Current year", "time-date", "pro", "system", "system"),
  v("today", "Today", "time-date", "starter", "system", "system"),
  v(
    "proposal_created_date",
    "Proposal created",
    "time-date",
    "pro",
    "system",
    "date",
  ),
  v(
    "proposal_expiry_date",
    "Proposal expiry",
    "time-date",
    "pro",
    "input",
    "date",
  ),
  // Date Operations
  v("date_plus_days", "Date +Xd", "date-operations", "team", "input", "date"),
  v("date_plus_months", "Date +Xm", "date-operations", "team", "input", "date"),
  v("date_format", "Date format", "date-operations", "team", "input", "date"),
  // Design
  v(
    "brand_primary_color",
    "Primary color",
    "design",
    "team",
    "account",
    "color",
  ),
];
